import { useRef } from "react";
import { Link, useParams } from "react-router-dom";
import { TOOLS, toolById } from "../data/tools";
import { useReveal } from "../hooks/useReveal";
import { TONE_BG } from "../lib/tones";
import { PageShell, Panel } from "../components/PageShell";
import { ResultReveal } from "../components/reveal/ResultReveal";
import { ToolControls, type ToolCtx } from "../components/tools/ToolControls";
import NotFound from "./NotFound";
import { ToolSeoSection } from "../components/seo/ToolSeoSection";
import { TOOL_SEO } from "../data/toolSeo";
import { faqJsonLd, SITE_URL, useSeo } from "../lib/seo";

export default function ToolPage() {
  const { toolId } = useParams();
  const tool = toolById(toolId);
  const { spec, show, close } = useReveal();
  const againRef = useRef<() => void>(() => undefined);
  const seo = tool ? TOOL_SEO[tool.id] : null;
  useSeo({
    title: seo?.title || "DITHER", description: seo?.description || "", path: `/tools/${toolId}`, noindex: !tool,
    jsonLd: seo ? [faqJsonLd(seo.faqs), { "@context": "https://schema.org", "@type": "WebApplication", name: tool.name, url: `${SITE_URL}/tools/${tool.id}`, applicationCategory: "GameApplication", operatingSystem: "Any", offers: { "@type": "Offer", price: "0", priceCurrency: "GBP" } }] : undefined,
  });
  if (!tool) return <NotFound />;

  const ctx: ToolCtx = {
    title: tool.name,
    againRef,
    close,
    emit: (o, title) => show({ kind: "tool", title: title || tool.name, result: o.result, detail: o.detail, anim: o.anim, animValue: o.animValue, reel: o.reel, podium: o.podium, category: tool.id }),
  };

  return (
    <PageShell title={tool.name} subtitle={tool.tagline} testId={`tool-page-${tool.id}`}>
      <Panel className="relative overflow-hidden">
        <span className={`absolute -right-8 -top-8 flex h-24 w-24 items-end justify-start rounded-full border-[3px] border-ink p-5 ${TONE_BG[tool.tone]}`} aria-hidden>
          <tool.icon className="h-7 w-7" strokeWidth={2.5} />
        </span>
        <div className="pt-6"><ToolControls key={tool.id} id={tool.id} ctx={ctx} /></div>
      </Panel>
      <ToolSeoSection id={tool.id} />
      <p className="mb-3 mt-6 text-xs font-extrabold uppercase tracking-widest text-zinc-500">Other tools</p>
      <div className="flex flex-wrap gap-2">
        {TOOLS.filter((t) => t.id !== tool.id).map((t) => (
          <Link key={t.id} to={`/tools/${t.id}`} className="chip hover:bg-pop-yellow" data-testid={`other-tool-${t.id}`}>{t.name}</Link>
        ))}
      </div>
      <ResultReveal spec={spec} onClose={close} onAgain={() => againRef.current()} againLabel={tool.id === "higher-or-lower" ? "Next" : "Again"} />
    </PageShell>
  );
}
